import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield, Globe } from 'lucide-react';

const DatenschutzPage = () => {
  const [language, setLanguage] = useState('de');

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="datenschutz-page">
      <Navbar />

      <main className="flex-1 py-12 px-6">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-babyblue-100 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              {language === 'de' ? 'Rechtliche Information' : 'Legal Information'}
            </div>
            <h1 className="font-heading text-4xl font-bold text-babyblue-900 mb-2">
              Datenschutzerklärung <span className="text-babyblue-500 font-normal">/ Privacy Policy</span>
            </h1>
            <p className="text-babyblue-600">
              {language === 'de' ? 'gemäß Art. 13 DSGVO' : 'pursuant to Art. 13 GDPR'}
            </p>
            <p className="text-sm text-babyblue-500 mt-1">
              {language === 'de' ? 'Stand: 2026' : 'Last updated: 2026'}
            </p>

            {/* Language Toggle */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <Globe className="w-5 h-5 text-babyblue-500" />
              <div className="flex bg-babyblue-100 rounded-full p-1">
                <button
                  onClick={() => setLanguage('de')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    language === 'de'
                      ? 'bg-primary text-white'
                      : 'text-babyblue-600 hover:text-babyblue-800'
                  }`}
                  data-testid="datenschutz-lang-de"
                >
                  Deutsch
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    language === 'en'
                      ? 'bg-primary text-white'
                      : 'text-babyblue-600 hover:text-babyblue-800'
                  }`}
                  data-testid="datenschutz-lang-en"
                >
                  English
                </button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-8 md:p-10 space-y-10 leading-relaxed text-babyblue-800">

            {language === 'de' ? (
              <>
                {/* 1 Verantwortlicher */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">1. Verantwortlicher</h2>
                  <p>
                    Verantwortlicher für die Datenverarbeitung auf dieser Website im Sinne der
                    Datenschutz-Grundverordnung (DSGVO) ist:
                  </p>
                  <div className="bg-babyblue-50 border border-babyblue-200 rounded-lg p-4 mt-3">
                    <p className="font-semibold text-babyblue-900">Teoman Talay</p>
                    <p className="text-sm mt-1">Germanatic – German Integration Platform</p>
                    <p className="text-sm mt-2">Marktstraße 5</p>
                    <p className="text-sm">72458 Albstadt</p>
                    <p className="text-sm">Deutschland</p>
                    <p className="text-sm mt-3">
                      E-Mail:{' '}
                      <a href="mailto:teomantalay21@gmail.com" className="text-primary underline font-medium">
                        teomantalay21@gmail.com
                      </a>
                    </p>
                    <p className="text-sm">
                      Telefon:{' '}
                      <a href="tel:+4915902221377" className="text-primary underline font-medium">
                        +49 1590 2221377
                      </a>
                    </p>
                    <p className="text-xs mt-3 text-babyblue-500">
                      Weitere Pflichtangaben finden Sie in unserem{' '}
                      <a href="/impressum" className="text-primary underline">Impressum</a>.
                    </p>
                  </div>
                </section>

                {/* 2 Geltungsbereich */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">2. Geltungsbereich</h2>
                  <p>
                    Diese Datenschutzerklärung gilt für die Nutzung der Website Germanatic. Sie informiert Sie über Art,
                    Umfang und Zweck der Verarbeitung personenbezogener Daten auf dieser Website sowie über Ihre Rechte
                    als betroffene Person.
                  </p>
                </section>

                {/* 3 Datenerhebung beim Besuch */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">3. Datenerhebung beim Besuch der Website</h2>
                  <p>
                    Beim Aufruf unserer Website werden durch unseren Webhosting-Provider automatisch Informationen in
                    sogenannten Server-Logfiles erfasst, die Ihr Browser übermittelt. Dies sind:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Browsertyp und Browserversion</li>
                    <li>Verwendetes Betriebssystem</li>
                    <li>Referrer-URL</li>
                    <li>Hostname des zugreifenden Rechners</li>
                    <li>Uhrzeit der Serveranfrage</li>
                    <li>IP-Adresse (gekürzt bzw. anonymisiert, sofern technisch möglich)</li>
                  </ul>
                  <p className="mt-3">
                    Eine Zusammenführung dieser Daten mit anderen Datenquellen erfolgt nicht. Die Datenverarbeitung
                    erfolgt auf Grundlage von <strong>Art. 6 Abs. 1 lit. f DSGVO</strong> (berechtigtes Interesse an
                    der technisch fehlerfreien Darstellung und der Sicherheit der Website).
                  </p>
                </section>

                {/* 4 Cookies */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">4. Cookies, lokaler Speicher und Werbung</h2>
                  <p>
                    Unsere Website verwendet zwei Kategorien von Speichermechanismen, die wir klar
                    voneinander trennen:
                  </p>
                  <ul className="list-disc pl-6 mt-3 space-y-2">
                    <li>
                      <strong>Technisch notwendige Speicherung:</strong> Wir speichern Ihre
                      Cookie-Auswahl im Cookie-Banner in Ihrem Browser (localStorage), damit
                      dieser nicht bei jedem Besuch erneut erscheint. Diese Speicherung erfolgt
                      ohne Einwilligung auf Grundlage von <strong>§ 25 Abs. 2 Nr. 2 TDDDG</strong>{' '}
                      (Telekommunikations-Digitale-Dienste-Datenschutz-Gesetz bzw. dessen
                      Nachfolgenormen) sowie <strong>Art. 6 Abs. 1 lit. f DSGVO</strong>.
                    </li>
                    <li>
                      <strong>Einwilligungspflichtige Speicherung (Werbung):</strong> Mit Ihrer
                      ausdrücklichen Einwilligung über unser Cookie-Banner setzen wir{' '}
                      <strong>Google AdSense</strong> ein. Dabei werden Cookies und vergleichbare
                      Technologien von Google verwendet (siehe Abschnitt 9). Sie können Ihre
                      Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen, indem Sie die
                      gespeicherte Einwilligung in Ihrem Browser löschen und Ihre Auswahl im
                      Cookie-Banner erneut treffen.
                    </li>
                  </ul>
                  <p className="mt-3">
                    Eigene Analyse-Cookies, Tracking-Cookies anderer Anbieter oder
                    Re-Targeting-Cookies setzen wir <strong>nicht</strong> ein.
                  </p>
                  <p className="mt-3 bg-babyblue-50 border-l-4 border-primary p-4 rounded text-sm">
                    💡 <strong>Auswahl jederzeit ändern:</strong> Sie können Ihre Cookie-Auswahl
                    jederzeit über den Link <em>„Cookie-Einstellungen / Cookie settings"</em> im
                    Footer dieser Seite öffnen und anpassen.
                  </p>
                </section>

                {/* 5 Bewertungsfunktion */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">5. Bewertungsfunktion (Reviews)</h2>
                  <p>
                    Wenn Sie eine Bewertung abgeben, verarbeiten wir folgende Daten:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Den von Ihnen <strong>freiwillig</strong> angegebenen Namen (oder „Anonym", wenn das Feld leer gelassen wird)</li>
                    <li>Ihre Sternebewertung (1–5)</li>
                    <li>Den Inhalt Ihres Bewertungstexts</li>
                    <li>Zeitstempel der Übermittlung</li>
                  </ul>
                  <p className="mt-3">
                    Diese Daten werden auf der Bewertungsseite öffentlich angezeigt. Die Verarbeitung erfolgt auf
                    Grundlage unseres berechtigten Interesses gemäß <strong>Art. 6 Abs. 1 lit. f DSGVO</strong>, den
                    Nutzern eine interaktive Feedback- und Bewertungsfunktion zur Verfügung zu stellen und die
                    Qualität unseres Angebots transparent darzustellen.
                  </p>
                  <p className="mt-2">
                    Sie können der Verarbeitung jederzeit widersprechen und die Löschung Ihrer Bewertung verlangen —
                    siehe Abschnitt „Ihre Rechte".
                  </p>
                </section>

                {/* 6 Sprachausgabe */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">6. Sprachausgabe (Web Speech API)</h2>
                  <p>
                    Beim Klick auf einen Vokabel- oder Satz-Button verwenden wir die <strong>Web Speech API</strong>{' '}
                    unseres Browsers. Die Verarbeitung der Sprachausgabe wird vom Browser gesteuert, und es werden
                    hierbei keine Daten an Germanatic übermittelt. Bitte beachten Sie, dass je nach verwendetem Browser
                    und Betriebssystem (insbesondere bei Google Chrome und Apple Safari) die Verarbeitung des Texts
                    durch externe Sprachsynthese-Dienste des jeweiligen Browser-Herstellers erfolgen kann. Hierfür
                    gilt die Datenschutzerklärung Ihres jeweiligen Browser-Anbieters.
                  </p>
                </section>

                {/* 7 Hosting */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">7. Hosting & Infrastruktur (Emergent)</h2>
                  <p>
                    Diese Website wird auf der Infrastruktur von <strong>Emergent (emergent.sh)</strong> betrieben.
                    Emergent stellt die Server und die MongoDB-Datenbank bereit, um die abgegebenen Bewertungen zu
                    speichern.
                  </p>
                  <p className="mt-3">
                    Aus technischen Gründen verarbeitet der Anbieter dabei Server-Logfiles (siehe Abschnitt 3) sowie
                    die Inhalte, die Sie über die Bewertungsfunktion absenden. Es besteht ein Vertrag zur
                    Auftragsverarbeitung gemäß <strong>Art. 28 DSGVO</strong>. Soweit Daten in Drittländer
                    übermittelt werden, erfolgt dies auf Grundlage geeigneter Garantien nach{' '}
                    <strong>Art. 46 DSGVO</strong>.
                  </p>
                </section>

                {/* 8 Google Forms */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">8. Feedback-Formulare (Google Forms)</h2>
                  <p>
                    Wir setzen <strong>Google Forms</strong> ein, um <strong>freiwillige</strong> Rückmeldungen und
                    Anregungen zur Verbesserung unseres Angebots zu erfassen. Die Teilnahme ist jederzeit freiwillig.
                  </p>
                  <p className="mt-3">
                    Wenn Sie ein Google-Formular ausfüllen, werden Ihre Antworten direkt von der{' '}
                    <strong>Google Ireland Limited</strong> (Gordon House, Barrow Street, Dublin 4, Irland) bzw.{' '}
                    <strong>Google LLC</strong> (1600 Amphitheatre Parkway, Mountain View, CA 94043, USA) verarbeitet
                    und gemäß den dortigen Datenschutzstandards gespeichert. Wir erhalten ausschließlich die von Ihnen
                    selbst eingegebenen Antworten.
                  </p>
                  <p className="mt-3">
                    Rechtsgrundlage ist Ihre Einwilligung gemäß <strong>Art. 6 Abs. 1 lit. a DSGVO</strong>, die Sie
                    durch das aktive Absenden des Formulars erteilen. Soweit Daten in die USA übermittelt werden,
                    erfolgt dies auf Grundlage geeigneter Garantien nach <strong>Art. 46 DSGVO</strong>
                    (Standardvertragsklauseln) bzw. — soweit anwendbar — auf Grundlage des EU-US Data Privacy
                    Frameworks.
                  </p>
                  <p className="mt-3">
                    Weitere Informationen zur Datenverarbeitung durch Google finden Sie in der{' '}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline font-medium"
                    >
                      Datenschutzerklärung von Google
                    </a>.
                  </p>
                </section>

                {/* 9 Google AdSense */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">9. Werbung (Google AdSense)</h2>
                  <p>
                    Zur Finanzierung dieses kostenfreien Angebots binden wir auf Grundlage Ihrer
                    <strong> ausdrücklichen Einwilligung</strong> Werbung des Dienstes{' '}
                    <strong>Google AdSense</strong> ein. Anbieter ist die{' '}
                    <strong>Google Ireland Limited</strong> (Gordon House, Barrow Street, Dublin 4,
                    Irland) bzw. die <strong>Google LLC</strong> (1600 Amphitheatre Parkway,
                    Mountain View, CA 94043, USA).
                  </p>
                  <p className="mt-3">
                    <strong>Welche Daten werden verarbeitet?</strong> Google AdSense verwendet
                    Cookies und vergleichbare Technologien, um Werbung auszuspielen, deren
                    Effektivität zu messen und Missbrauch (z. B. Klickbetrug) zu verhindern. Dabei
                    können insbesondere folgende Daten verarbeitet werden: IP-Adresse,
                    Geräteinformationen (Browser, Betriebssystem), Spracheinstellung, ungefähre
                    Standortinformation (Land/Region), besuchte Seiten innerhalb unseres Angebots,
                    Klick- und Interaktionsdaten mit Anzeigen sowie pseudonyme Werbe-Kennungen.
                  </p>
                  <p className="mt-3">
                    <strong>Rechtsgrundlage:</strong> Die Verarbeitung erfolgt ausschließlich auf
                    Grundlage Ihrer Einwilligung gemäß <strong>Art. 6 Abs. 1 lit. a DSGVO</strong>{' '}
                    sowie <strong>§ 25 Abs. 1 TDDDG</strong>. Die Einwilligung erteilen Sie aktiv
                    über unser Cookie-Banner. Sie können diese Einwilligung jederzeit mit Wirkung
                    für die Zukunft widerrufen, indem Sie Ihre Auswahl im Cookie-Banner ändern bzw.
                    die im Browser gespeicherte Einwilligung löschen.
                  </p>
                  <p className="mt-3">
                    <strong>Google Consent Mode v2:</strong> Wir setzen Googles „Consent Mode v2"
                    ein. Ohne bzw. mit verweigerter Einwilligung werden ausschließlich{' '}
                    <strong>nicht-personalisierte Anzeigen</strong> ausgespielt; in diesem Modus
                    werden <strong>keine Profile</strong> zu Ihrer Person gebildet und es findet
                    keine interessenbasierte Auswertung Ihres Surfverhaltens statt. Google kann
                    in diesem Modus jedoch weiterhin <strong>minimale Identifier oder Cookies</strong>{' '}
                    setzen bzw. Daten verarbeiten, soweit dies für betriebsnotwendige Zwecke
                    erforderlich ist — insbesondere zur Anzeigenzählung (z. B. Frequency Capping),
                    zur aggregierten Berichterstattung sowie zur Betrugs- und Missbrauchsprävention.
                    Diese Verarbeitungen erfolgen im gesetzlich zulässigen Rahmen. Erst nach Ihrer
                    ausdrücklichen Einwilligung („Alle akzeptieren" bzw. aktivierter
                    Werbe-Schalter) werden personalisierte Anzeigen und die hierfür erforderlichen
                    Cookies aktiviert.
                  </p>
                  <p className="mt-3">
                    <strong>Drittlandübermittlung:</strong> Es findet eine Übermittlung
                    personenbezogener Daten in die USA statt. Google ist nach dem EU-US Data
                    Privacy Framework zertifiziert. Ergänzend werden EU-Standardvertragsklauseln
                    nach <strong>Art. 46 Abs. 2 lit. c DSGVO</strong> verwendet.
                  </p>
                  <p className="mt-3">
                    <strong>Datenschutzrechtliche Rolle:</strong> Google handelt im Rahmen von
                    AdSense als <strong>eigenständig Verantwortlicher</strong> (kein
                    Auftragsverarbeitungsverhältnis nach Art. 28 DSGVO). Die Verpflichtung beider
                    Seiten auf die „Datenverarbeitungsbedingungen zwischen Verantwortlichen für
                    Google-Werbeprodukte" ist Teil der AdSense-Nutzungsbedingungen, denen wir
                    zugestimmt haben.
                  </p>
                  <p className="mt-3">
                    <strong>Speicherdauer:</strong> Die Speicherdauer der von Google AdSense
                    gesetzten Cookies und der dort verarbeiteten Daten richtet sich nach den
                    Regelungen von Google. Wir selbst speichern hierzu keine personenbezogenen
                    Daten.
                  </p>
                  <p className="mt-3">
                    Weitere Informationen sowie Möglichkeiten der personalisierten Werbung zu
                    widersprechen finden Sie hier:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>
                      <a
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline"
                      >
                        Datenschutzerklärung von Google
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://policies.google.com/technologies/partner-sites"
                        target="_blank"
                        rel="noopener noreferrer"
                        referrerPolicy="no-referrer"
                        className="text-primary underline"
                      >
                        Wie Google Daten von Websites oder Apps verwendet
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.youronlinechoices.com/de/"
                        target="_blank"
                        rel="noopener noreferrer"
                        referrerPolicy="no-referrer"
                        className="text-primary underline"
                      >
                        Personalisierte Werbung deaktivieren (YourOnlineChoices)
                      </a>
                    </li>
                  </ul>
                </section>

                {/* 10 Ihre Rechte */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">10. Ihre Rechte als betroffene Person</h2>
                  <p>Ihnen stehen folgende Rechte zu:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Auskunft</strong> (Art. 15 DSGVO): Welche Daten wir über Sie speichern.</li>
                    <li><strong>Berichtigung</strong> (Art. 16 DSGVO): Berichtigung falscher oder unvollständiger Daten.</li>
                    <li><strong>Löschung</strong> (Art. 17 DSGVO): „Recht auf Vergessenwerden".</li>
                    <li><strong>Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO).</li>
                    <li><strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO).</li>
                    <li><strong>Widerspruch</strong> (Art. 21 DSGVO): Gegen Verarbeitungen auf Grundlage berechtigter Interessen.</li>
                    <li><strong>Widerruf</strong> (Art. 7 Abs. 3 DSGVO): Widerruf erteilter Einwilligungen mit Wirkung für die Zukunft.</li>
                    <li><strong>Beschwerde bei einer Aufsichtsbehörde</strong> (Art. 77 DSGVO): z. B. der für Sie zuständigen Landesdatenschutzbehörde.</li>
                  </ul>
                  <p className="mt-3">
                    Zur Ausübung dieser Rechte wenden Sie sich bitte formlos an{' '}
                    <a href="mailto:teomantalay21@gmail.com" className="text-primary underline font-medium">
                      teomantalay21@gmail.com
                    </a>{' '}
                    oder an die im{' '}
                    <a href="/impressum" className="text-primary underline font-medium">Impressum</a> genannten Kontaktdaten.
                  </p>
                </section>

                {/* 11 Speicherdauer */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">11. Speicherdauer</h2>
                  <p>
                    Server-Logfiles werden für die Dauer von maximal 14 Tagen aus Sicherheitsgründen gespeichert und
                    anschließend gelöscht. Bewertungen bleiben gespeichert, bis Sie deren Löschung verlangen oder wir
                    diese berechtigterweise entfernen.
                  </p>
                </section>

                {/* 12 Keine automatisierte Entscheidung */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">12. Keine automatisierte Entscheidungsfindung</h2>
                  <p>
                    Eine automatisierte Entscheidungsfindung einschließlich Profiling gemäß Art. 22 DSGVO findet auf
                    dieser Website nicht statt.
                  </p>
                </section>

                {/* 13 Änderungen */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">13. Änderungen dieser Datenschutzerklärung</h2>
                  <p>
                    Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen
                    rechtlichen Anforderungen entspricht oder Änderungen unserer Leistungen umgesetzt werden können.
                    Maßgeblich ist jeweils die zum Zeitpunkt Ihres Besuchs hier veröffentlichte Fassung.
                  </p>
                </section>
              </>
            ) : (
              <>
                {/* 1 Controller */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">1. Controller</h2>
                  <p>
                    The controller responsible for data processing on this website within the meaning of the
                    General Data Protection Regulation (GDPR) is:
                  </p>
                  <div className="bg-babyblue-50 border border-babyblue-200 rounded-lg p-4 mt-3">
                    <p className="font-semibold text-babyblue-900">Teoman Talay</p>
                    <p className="text-sm mt-1">Germanatic – German Integration Platform</p>
                    <p className="text-sm mt-2">Marktstraße 5</p>
                    <p className="text-sm">72458 Albstadt</p>
                    <p className="text-sm">Germany</p>
                    <p className="text-sm mt-3">
                      Email:{' '}
                      <a href="mailto:teomantalay21@gmail.com" className="text-primary underline font-medium">
                        teomantalay21@gmail.com
                      </a>
                    </p>
                    <p className="text-sm">
                      Phone:{' '}
                      <a href="tel:+4915902221377" className="text-primary underline font-medium">
                        +49 1590 2221377
                      </a>
                    </p>
                    <p className="text-xs mt-3 text-babyblue-500">
                      Further legally required information can be found in our{' '}
                      <a href="/impressum" className="text-primary underline">Legal Notice (Impressum)</a>.
                    </p>
                  </div>
                </section>

                {/* 2 Scope */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">2. Scope</h2>
                  <p>
                    This Privacy Policy applies to the use of the Germanatic website. It informs you about the nature,
                    scope and purpose of the processing of personal data on this website as well as about your rights
                    as a data subject.
                  </p>
                </section>

                {/* 3 Data Collection on Visit */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">3. Data Collection When Visiting the Website</h2>
                  <p>
                    When you visit our website, our hosting provider automatically collects information in so-called
                    server log files that your browser transmits. This includes:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Browser type and browser version</li>
                    <li>Operating system used</li>
                    <li>Referrer URL</li>
                    <li>Hostname of the accessing computer</li>
                    <li>Time of the server request</li>
                    <li>IP address (shortened or anonymised where technically possible)</li>
                  </ul>
                  <p className="mt-3">
                    This data is not merged with other data sources. The processing is based on{' '}
                    <strong>Art. 6 (1) lit. f GDPR</strong> (legitimate interest in the technically error-free
                    presentation and security of the website).
                  </p>
                </section>

                {/* 4 Cookies */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">4. Cookies, Local Storage and Advertising</h2>
                  <p>
                    Our website uses two categories of storage mechanisms, which we clearly
                    separate:
                  </p>
                  <ul className="list-disc pl-6 mt-3 space-y-2">
                    <li>
                      <strong>Technically necessary storage:</strong> We store your cookie choice
                      in your browser (localStorage) so that the cookie banner does not reappear
                      on every visit. This storage takes place without consent on the basis of{' '}
                      <strong>§ 25 (2) No. 2 TDDDG</strong> (German telecommunications privacy
                      law / successor provisions) and <strong>Art. 6 (1) lit. f GDPR</strong>.
                    </li>
                    <li>
                      <strong>Consent-based storage (advertising):</strong> With your explicit
                      consent via our cookie banner, we use <strong>Google AdSense</strong>.
                      Cookies and comparable technologies from Google are set in this context
                      (see Section 9). You may revoke your consent at any time with effect for
                      the future by deleting the stored consent in your browser and choosing
                      again in the cookie banner.
                    </li>
                  </ul>
                  <p className="mt-3">
                    We do <strong>not</strong> use our own analytics cookies, third-party tracking
                    cookies or retargeting cookies.
                  </p>
                  <p className="mt-3 bg-babyblue-50 border-l-4 border-primary p-4 rounded text-sm">
                    💡 <strong>Change your choice anytime:</strong> You can re-open and adjust
                    your cookie choice at any time via the <em>"Cookie-Einstellungen / Cookie
                    settings"</em> link in the footer of this page.
                  </p>
                </section>

                {/* 5 Reviews */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">5. Review Function</h2>
                  <p>When you submit a review, we process the following data:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>the name you <strong>voluntarily</strong> provide (or "Anonymous" if left blank)</li>
                    <li>your star rating (1–5)</li>
                    <li>the content of your review text</li>
                    <li>timestamp of submission</li>
                  </ul>
                  <p className="mt-3">
                    This data is displayed publicly on the reviews page. Processing is based on our legitimate
                    interest pursuant to <strong>Art. 6 (1) lit. f GDPR</strong> in providing users with an
                    interactive feedback and review function and in transparently representing the quality of our
                    service.
                  </p>
                  <p className="mt-2">
                    You may object to the processing at any time and request deletion of your review — see "Your Rights".
                  </p>
                </section>

                {/* 6 Web Speech API */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">6. Speech Output (Web Speech API)</h2>
                  <p>
                    When you click on a vocabulary or sentence button, we use the <strong>Web Speech API</strong> of
                    your browser. Speech output is controlled by the browser itself, and no data is transmitted to
                    Germanatic in this process. Please note that depending on the browser and operating system you use
                    (in particular Google Chrome and Apple Safari), the text may be processed by external speech
                    synthesis services of the respective browser manufacturer. The privacy policy of your respective
                    browser provider applies in this case.
                  </p>
                </section>

                {/* 7 Hosting */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">7. Hosting & Infrastructure (Emergent)</h2>
                  <p>
                    This website is hosted on the infrastructure of <strong>Emergent (emergent.sh)</strong>, which
                    provides servers and the MongoDB database used to store user reviews.
                  </p>
                  <p className="mt-3">
                    To operate the site securely, Emergent processes standard server log files (as detailed in
                    Section 3) and the content you actively submit when writing a review. A data processing
                    agreement pursuant to <strong>Art. 28 GDPR</strong> ensures that your data is handled strictly
                    in accordance with European data protection standards. Where data is transferred to third
                    countries, this is carried out on the basis of appropriate safeguards under{' '}
                    <strong>Art. 46 GDPR</strong>.
                  </p>
                </section>

                {/* 8 Google Forms */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">8. Feedback Forms (Google Forms)</h2>
                  <p>
                    We use <strong>Google Forms</strong> to collect <strong>voluntary</strong> feedback and
                    suggestions for improving our service. Participation is entirely voluntary.
                  </p>
                  <p className="mt-3">
                    When you fill out a Google Form, your responses are processed directly by{' '}
                    <strong>Google Ireland Limited</strong> (Gordon House, Barrow Street, Dublin 4, Ireland) and{' '}
                    <strong>Google LLC</strong> (1600 Amphitheatre Parkway, Mountain View, CA 94043, USA) and
                    stored in accordance with their privacy standards. We only receive the answers you provide
                    yourself.
                  </p>
                  <p className="mt-3">
                    The legal basis is your consent pursuant to <strong>Art. 6 (1) lit. a GDPR</strong>, which you
                    give by actively submitting the form. Where data is transferred to the USA, this is carried
                    out on the basis of appropriate safeguards under <strong>Art. 46 GDPR</strong> (Standard
                    Contractual Clauses) or — where applicable — on the basis of the EU-US Data Privacy Framework.
                  </p>
                  <p className="mt-3">
                    For more information about Google's data processing, please see Google's{' '}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline font-medium"
                    >
                      Privacy Policy
                    </a>.
                  </p>
                </section>

                {/* 9 Google AdSense */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">9. Advertising (Google AdSense)</h2>
                  <p>
                    To finance this free service, we display advertising from{' '}
                    <strong>Google AdSense</strong> based on your <strong>explicit consent</strong>.
                    The provider is <strong>Google Ireland Limited</strong> (Gordon House, Barrow
                    Street, Dublin 4, Ireland) and <strong>Google LLC</strong> (1600 Amphitheatre
                    Parkway, Mountain View, CA 94043, USA).
                  </p>
                  <p className="mt-3">
                    <strong>What data is processed?</strong> Google AdSense uses cookies and
                    comparable technologies to deliver ads, measure their effectiveness and prevent
                    misuse (e.g. click fraud). In particular the following data may be processed:
                    IP address, device information (browser, operating system), language settings,
                    approximate location (country/region), pages visited within our service, click
                    and interaction data with ads, and pseudonymous advertising identifiers.
                  </p>
                  <p className="mt-3">
                    <strong>Legal basis:</strong> Processing takes place exclusively on the basis
                    of your consent pursuant to <strong>Art. 6 (1) lit. a GDPR</strong> and{' '}
                    <strong>§ 25 (1) TDDDG</strong>. You actively give this consent via our cookie
                    banner. You may revoke this consent at any time with effect for the future by
                    changing your choice in the cookie banner or deleting the consent stored in
                    your browser.
                  </p>
                  <p className="mt-3">
                    <strong>Google Consent Mode v2:</strong> We use Google's "Consent Mode v2".
                    Without or with refused consent, only <strong>non-personalised ads</strong>{' '}
                    are served; in this mode <strong>no profiles</strong> are formed about you
                    and no interest-based analysis of your browsing behaviour takes place. In
                    this mode, however, Google may still set <strong>minimal identifiers or
                    cookies</strong> and process data to the extent necessary for operational
                    purposes — in particular for ad counting (e.g. frequency capping), aggregated
                    reporting and fraud / abuse prevention. These processings occur within the
                    legally permitted framework. Personalised ads and the cookies required for
                    them are activated only after your explicit consent ("Accept all" or the
                    advertising toggle enabled).
                  </p>
                  <p className="mt-3">
                    <strong>Third-country transfers:</strong> Personal data is transferred to the
                    USA. Google is certified under the EU-US Data Privacy Framework. In addition,
                    EU Standard Contractual Clauses pursuant to{' '}
                    <strong>Art. 46 (2) lit. c GDPR</strong> are used.
                  </p>
                  <p className="mt-3">
                    <strong>Data-protection role:</strong> In connection with AdSense, Google
                    acts as an <strong>independent controller</strong> (not as a processor under
                    Art. 28 GDPR). The mutual commitment to Google's "Controller-Controller Data
                    Protection Terms for Google Advertising Products" is part of the AdSense
                    Terms of Service, which we have accepted.
                  </p>
                  <p className="mt-3">
                    <strong>Storage duration:</strong> The storage duration of cookies set by
                    Google AdSense and the data processed there is governed by Google's policies.
                    We ourselves do not store any personal data related to this.
                  </p>
                  <p className="mt-3">
                    Further information and options to object to personalised advertising can be
                    found here:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>
                      <a
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline"
                      >
                        Google Privacy Policy
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://policies.google.com/technologies/partner-sites"
                        target="_blank"
                        rel="noopener noreferrer"
                        referrerPolicy="no-referrer"
                        className="text-primary underline"
                      >
                        How Google uses data from sites or apps
                      </a>
                    </li>
                    <li>
                      <a
                        href="https://www.youronlinechoices.com/uk/your-ad-choices"
                        target="_blank"
                        rel="noopener noreferrer"
                        referrerPolicy="no-referrer"
                        className="text-primary underline"
                      >
                        Opt out of personalised advertising (YourOnlineChoices)
                      </a>
                    </li>
                  </ul>
                </section>

                {/* 10 Your Rights */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">10. Your Rights as a Data Subject</h2>
                  <p>You have the following rights:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li><strong>Right of access</strong> (Art. 15 GDPR): What data we store about you.</li>
                    <li><strong>Right to rectification</strong> (Art. 16 GDPR): Correction of incorrect or incomplete data.</li>
                    <li><strong>Right to erasure</strong> (Art. 17 GDPR): "Right to be forgotten".</li>
                    <li><strong>Right to restriction of processing</strong> (Art. 18 GDPR).</li>
                    <li><strong>Right to data portability</strong> (Art. 20 GDPR).</li>
                    <li><strong>Right to object</strong> (Art. 21 GDPR): Against processing based on legitimate interests.</li>
                    <li><strong>Right to withdraw consent</strong> (Art. 7 (3) GDPR): With effect for the future.</li>
                    <li><strong>Right to lodge a complaint with a supervisory authority</strong> (Art. 77 GDPR): E.g. with the state data protection authority responsible for you.</li>
                  </ul>
                  <p className="mt-3">
                    To exercise these rights, please contact us informally at{' '}
                    <a href="mailto:teomantalay21@gmail.com" className="text-primary underline font-medium">
                      teomantalay21@gmail.com
                    </a>{' '}
                    or via the contact details listed in our{' '}
                    <a href="/impressum" className="text-primary underline font-medium">Legal Notice</a>.
                  </p>
                </section>

                {/* 11 Storage Duration */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">11. Storage Duration</h2>
                  <p>
                    Server log files are stored for a maximum of 14 days for security reasons and then deleted.
                    Reviews remain stored until you request their deletion or until we delete them on legitimate
                    grounds.
                  </p>
                </section>

                {/* 12 No Automated Decision-Making */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">12. No Automated Decision-Making</h2>
                  <p>
                    No automated decision-making, including profiling, pursuant to Art. 22 GDPR takes place on this
                    website.
                  </p>
                </section>

                {/* 11 Changes */}
                <section>
                  <h2 className="font-heading text-2xl font-bold text-babyblue-900 mb-3">13. Changes to this Privacy Policy</h2>
                  <p>
                    We reserve the right to adapt this Privacy Policy so that it always complies with current legal
                    requirements or to implement changes to our services. The version published here at the time of
                    your visit is the applicable one.
                  </p>
                </section>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DatenschutzPage;
